/* eslint-disable @typescript-eslint/no-extraneous-class -- needed*/
import { type InterfaceEntity, InterfaceType } from '@unifyapps/defs/types/interface';
import { InterfacePageEntityComponentType } from '@unifyapps/defs/types/page';
import { fallbackLng } from '@unifyapps/i18n/settings';
import { EMPTY_ARRAY } from '../../../../consts/empty';
import type { InterfaceComponent } from '../../../types/interfaceComponent';
import { InterfaceModes } from '../types';

interface GetApplicationLocaleParams {
  interfaceRecord?: InterfaceEntity;
  userLocale?: string;
  mode?: InterfaceModes;
  localeFromCookie?: string;
}

class InterfaceHelper {
  static commonNamespaceComponentId = 'common';

  static getInterfaceType = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.type || InterfaceType.Application;
  };

  static getInterfacePlatform = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.manifest?.type || 'WEB';
  };

  static getInterfaceSecurityDetails = (
    interfaceRecord: Partial<InterfaceEntity> | InterfaceEntity | undefined,
  ) => {
    return interfaceRecord?.properties?.package?.security ?? interfaceRecord?.properties?.security;
  };

  static isPublicInterface = (interfaceRecord: InterfaceEntity | undefined) => {
    return this.getInterfaceSecurityDetails(interfaceRecord)?.type === 'PUBLIC';
  };

  static getDefaultLocale = (interfaceRecord: InterfaceEntity | undefined) => {
    return interfaceRecord?.properties.locale?.defaultLocale;
  };

  static getLocale = (params: GetApplicationLocaleParams): string => {
    const { userLocale, interfaceRecord, mode = InterfaceModes.RUNNER, localeFromCookie } = params;
    const defaultLocale = this.getDefaultLocale(interfaceRecord);
    const browserLocale =
      typeof navigator !== 'undefined' && navigator.language ? navigator.language : undefined;
    const allLocales = this.getAllLocalesSet(interfaceRecord);

    const hasLocaleFromCookie = localeFromCookie !== undefined && allLocales.has(localeFromCookie);
    const hasUserLocale = userLocale !== undefined && allLocales.has(userLocale);
    const hasBrowserLocale = Boolean(browserLocale && allLocales.has(browserLocale));

    if (hasLocaleFromCookie) {
      return localeFromCookie;
    } else if (mode === InterfaceModes.BUILDER && defaultLocale !== undefined) {
      return defaultLocale;
    } else if (hasUserLocale) {
      return userLocale;
    } else if (hasBrowserLocale) {
      return browserLocale || fallbackLng;
    }
    return defaultLocale || fallbackLng;
  };

  static getBuilderLocale = (userLocale?: string, interfaceRecord?: InterfaceEntity) => {
    const defaultLocale = this.getDefaultLocale(interfaceRecord);
    return defaultLocale ?? userLocale ?? fallbackLng;
  };

  static getSupportedLocales = (interfaceRecord: InterfaceEntity | undefined) => {
    return (interfaceRecord?.properties.locale?.supportedLocales || EMPTY_ARRAY) as string[];
  };

  static getAllLocalesSet = (interfaceRecord: InterfaceEntity | undefined) => {
    const interfaceLocales = new Set<string>();
    const locale = interfaceRecord?.properties.locale;

    if (!locale) {
      return interfaceLocales;
    }

    interfaceLocales.add(locale.defaultLocale);

    this.getSupportedLocales(interfaceRecord).forEach((supportedLocale) => {
      interfaceLocales.add(supportedLocale);
    });

    return interfaceLocales;
  };

  static getInterfacePageIdFromSlug = (
    interfaceComponent: InterfaceComponent,
    interfacePageIdVsSlugMap?: Record<string, string | undefined>,
  ) => {
    switch (interfaceComponent.type) {
      case InterfacePageEntityComponentType.WALKTHROUGH:
      case InterfacePageEntityComponentType.MODULE: {
        return interfaceComponent.id;
      }
      case InterfacePageEntityComponentType.PAGE: {
        const interfacePageIdVsSlugMapEntries = Object.entries(interfacePageIdVsSlugMap ?? {});
        return interfacePageIdVsSlugMapEntries.find(
          ([, value]) => value === interfaceComponent.slug,
        )?.[0];
      }
    }
  };

  static getFlags(interfaceRecord: Partial<InterfaceEntity> | undefined) {
    return interfaceRecord?.properties?.flags;
  }

  static getTheme(interfaceRecord: Partial<InterfaceEntity> | undefined) {
    return interfaceRecord?.properties?.theme;
  }

  static customCode(interfaceRecord: Partial<InterfaceEntity> | undefined) {
    return interfaceRecord?.properties?.customCode;
  }
}

export default InterfaceHelper;
